import { useEffect, useMemo, useRef, useState } from "react";
import { useCompanyInfo, useUpdateCompany } from "@components/shared/hooks/useCompany";
import { defaultInvoiceCustomStyle } from "../constants/defaultInvoiceCustomStyle";
import type { InvoiceCustomStyle } from "../types/invoiceCustomStyle.types";
import { normalizeInvoiceCustomStyle, toInvoiceCustomStylePayload } from "../utils/normalizeInvoiceCustomStyle";

export const useInvoiceCustomStyle = () => {
  const { data: company, isError } = useCompanyInfo();
  const updateCompanyMutation = useUpdateCompany();

  const hydratedRef = useRef(false);

  const [isHydrated, setIsHydrated] = useState(false);
  const [saved, setSaved] = useState<InvoiceCustomStyle>(() => structuredClone(defaultInvoiceCustomStyle));
  const [value, setValue] = useState<InvoiceCustomStyle>(() => structuredClone(defaultInvoiceCustomStyle));

  useEffect(() => {
    if (hydratedRef.current) return;
    if (company === undefined && !isError) return;

    hydratedRef.current = true;

    const hydrated = normalizeInvoiceCustomStyle((company as any)?.invoiceCustomStyle);

    setSaved(hydrated);
    setValue(hydrated);
    setIsHydrated(true);
  }, [company, isError]);

  const isDirty = useMemo(() => JSON.stringify(value) !== JSON.stringify(saved), [value, saved]);

  const reset = () => setValue(structuredClone(saved));

  const save = () => {
    if (!isDirty || updateCompanyMutation.isPending) return;

    const pending = value;

    updateCompanyMutation.mutate(
      { invoiceCustomStyle: toInvoiceCustomStylePayload(pending) },
      {
        onSuccess: () => setSaved(pending),
      },
    );
  };

  return {
    value,
    setValue,
    saved,
    isHydrated,
    isDirty,
    isSaving: updateCompanyMutation.isPending,
    reset,
    save,
  };
};

export type InvoiceCustomStyleController = ReturnType<typeof useInvoiceCustomStyle>;
